import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { coursService, authService } from '../../api';
import { 
  FiArrowLeft, FiUser, FiClock, FiBookOpen, FiStar, 
  FiCheckCircle, FiPlay, FiFileText, 
  FiVideo, FiFile, FiChevronRight, FiDownload,
  FiUsers, FiX, FiAlertCircle
} from 'react-icons/fi';
import './DetailCours.css';

const DetailCours = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cours, setCours] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInscrit, setIsInscrit] = useState(false);
  const [progression, setProgression] = useState(null);
  const [user, setUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmCallback, setConfirmCallback] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    const checkAuthAndFetch = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const userData = await authService.getProfile();
          setUser(userData);
        }
      } catch (error) {
        console.log('Utilisateur non authentifié');
      }
      fetchCours();
    };

    checkAuthAndFetch();
  }, [id]);

  const fetchCours = async () => {
    setLoading(true);
    try {
      const response = await coursService.getOne(id);
      setCours(response.data);
      
      if (user) {
        checkInscription();
      }
    } catch (err) {
      console.error('Erreur chargement cours:', err);
      setError('Impossible de charger le cours. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const checkInscription = async () => {
    try {
      const response = await coursService.getMesInscriptions();
      if (response && response.data) {
        const inscriptions = response.data.data || [];
        const trouve = inscriptions.find(ins => ins.cours_id === parseInt(id) || ins.cours?.id === parseInt(id));
        setIsInscrit(!!trouve);
        if (trouve) {
          setProgression({
            progression: trouve.progression || 0,
            statut: trouve.statut
          });
        }
      }
    } catch (err) {
      console.error('Erreur vérification inscription:', err);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 5000);
  };

  const handleConfirmAction = (message, callback) => {
    setConfirmMessage(message);
    setConfirmCallback(() => callback);
    setShowConfirm(true);
  };

  const handleConfirm = () => {
    if (confirmCallback) {
      confirmCallback();
    }
    setShowConfirm(false);
    setConfirmCallback(null);
  };

  const handleCancel = () => {
    setShowConfirm(false);
    setConfirmCallback(null);
  };

  const handleInscription = async () => {
    if (!user) {
      navigate('/connexion');
      return;
    }

    setActionLoading(true);
    try {
      await coursService.inscrire(id);
      setIsInscrit(true);
      await checkInscription();
      await fetchCours();
      showNotification('Inscription au cours réussie', 'success');
    } catch (err) {
      console.error('Erreur inscription:', err);
      showNotification('Erreur lors de l\'inscription. Veuillez réessayer.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDesinscription = () => {
    handleConfirmAction('Êtes-vous sûr de vouloir vous désinscrire de ce cours ?', async () => {
      setActionLoading(true);
      try {
        await coursService.desinscrire(id);
        setIsInscrit(false);
        setProgression(null);
        await fetchCours();
        showNotification('Désinscription réussie.', 'info');
      } catch (err) {
        console.error('Erreur désinscription:', err);
        showNotification('Erreur lors de la désinscription. Veuillez réessayer.', 'error');
      } finally {
        setActionLoading(false);
      }
    });
  };

  const handleMarquerTermine = async (leconId) => {
    try {
      await coursService.marquerLeconTerminee(id, leconId);
      await checkInscription();
      showNotification('Leçon marquée comme terminée', 'success');
    } catch (err) {
      console.error('Erreur marquage leçon:', err);
      showNotification('Erreur lors du marquage de la leçon.', 'error');
    }
  };

  const formatPrice = (price) => {
    if (price === 0 || price === null || price === undefined) return 'Gratuit';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const getNiveauBadge = (niveau) => {
    const colors = {
      'débutant': 'badge-debutant',
      'intermédiaire': 'badge-intermediaire',
      'avancé': 'badge-avance'
    };
    return colors[niveau] || 'badge-debutant';
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'video': return <FiVideo />;
      case 'pdf': return <FiFile />;
      case 'quiz': return <FiStar />;
      default: return <FiFileText />;
    }
  };

  if (loading) {
    return (
      <div className="detail-loading">
        <div className="spinner"></div>
        <p>Chargement du cours...</p>
      </div>
    );
  }

  if (error || !cours) {
    return (
      <div className="detail-error">
        <p>{error || 'Cours non trouvé'}</p>
        <Link to="/cours" className="btn-back-link">Retour aux cours</Link>
      </div>
    );
  }

  const estMentor = user && user.id === cours.mentor_id;

  return (
    <div className="detail-wrapper">
      {/* Notification */}
      {notification.show && (
        <div className={`toast ${notification.type}`}>
          <span>{notification.message}</span>
          <button onClick={() => setNotification({ show: false, message: '', type: '' })}>
            <FiX />
          </button>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="modal-overlay" onClick={handleCancel}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h3>Confirmation</h3>
            <p>{confirmMessage}</p>
            <div className="modal-actions">
              <button className="btn-modal-cancel" onClick={handleCancel}>Annuler</button>
              <button className="btn-modal-confirm" onClick={handleConfirm}>Confirmer</button>
            </div>
          </div>
        </div>
      )}

      <div className="detail-back">
        <Link to="/cours" className="btn-back">
          <FiArrowLeft /> Retour aux cours
        </Link>
      </div>

      <div className="detail-header">
        <div className="detail-image">
          <img 
            src={cours.image_url || '/default-cours.jpg'} 
            alt={cours.titre}
            onError={(e) => { e.target.src = '/default-cours.jpg'; }}
          />
          <span className={`detail-badge ${getNiveauBadge(cours.niveau)}`}>
            {cours.niveau}
          </span>
        </div>

        <div className="detail-info">
          <h1>{cours.titre}</h1>
          <div className="detail-meta">
            <span className="meta-item">
              <FiUser className="meta-icon" />
              {cours.mentor?.prenom || 'Mentor'}
            </span>
            <span className="meta-item">
              <FiUsers className="meta-icon" />
              {cours.nombre_etudiants || 0} étudiants
            </span>
            {cours.duree_estimee && (
              <span className="meta-item">
                <FiClock className="meta-icon" />
                {Math.floor(cours.duree_estimee / 60)}h
              </span>
            )}
            <span className="meta-item">
              <FiBookOpen className="meta-icon" />
              {cours.lecons?.length || 0} leçons
            </span>
          </div>
          <p className="detail-description">{cours.description}</p>
          
          <div className="detail-actions">
            <span className="detail-price">{formatPrice(cours.prix)}</span>
            
            {estMentor ? (
              <div className="detail-mentor-actions">
                <Link to={`/cours/${cours.id}/modifier`} className="btn-edit">
                  Modifier
                </Link>
                <Link to={`/cours/${cours.id}/lecons`} className="btn-manage">
                  Gérer les leçons
                </Link>
              </div>
            ) : (
              <>
                {isInscrit ? (
                  <div className="detail-inscrit-actions">
                    <span className="btn-inscrit">
                      <FiCheckCircle /> Inscrit
                    </span>
                    {progression && progression.progression > 0 && (
                      <span className="progress-text">
                        Progression: {progression.progression}%
                      </span>
                    )}
                    <button 
                      onClick={handleDesinscription}
                      className="btn-unsubscribe"
                      disabled={actionLoading}
                    >
                      Se désinscrire
                    </button>
                  </div>
                ) : (
                  <button 
                    onClick={handleInscription}
                    className="btn-subscribe"
                    disabled={actionLoading || cours.statut !== 'publié'}
                  >
                    {actionLoading ? 'Inscription...' : "S'inscrire au cours"}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Leçons du cours */}
      {cours.lecons && cours.lecons.length > 0 && (
        <div className="detail-lessons">
          <h2>Contenu du cours</h2>
          <div className="lessons-list">
            {cours.lecons.map((lecon, index) => {
              const estComplete = isInscrit && lecon.est_complete_par_user;
              
              return (
                <div key={lecon.id} className={`lesson-item ${estComplete ? 'complete' : ''}`}>
                  <div className="lesson-number">{index + 1}</div>
                  <div className="lesson-content">
                    <div className="lesson-header">
                      <div className="lesson-type-icon">
                        {getTypeIcon(lecon.type)}
                      </div>
                      <div className="lesson-title-wrap">
                        <h3>{lecon.titre}</h3>
                        <span className="lesson-type">{lecon.type}</span>
                      </div>
                    </div>
                    {lecon.contenu && (
                      <p className="lesson-text">{lecon.contenu}</p>
                    )}
                    {lecon.video_url && (
                      <a href={lecon.video_url} target="_blank" rel="noopener noreferrer" className="lesson-link">
                        <FiPlay /> Voir la vidéo
                      </a>
                    )}
                    {lecon.fichier_url && (
                      <a href={lecon.fichier_url} download className="lesson-link">
                        <FiDownload /> Télécharger
                      </a>
                    )}
                  </div>
                  {isInscrit && !estMentor && (
                    <div className="lesson-action">
                      {estComplete ? (
                        <span className="lesson-done">
                          <FiCheckCircle /> Terminé
                        </span>
                      ) : (
                        <button 
                          onClick={() => handleMarquerTermine(lecon.id)}
                          className="lesson-mark-btn"
                        >
                          Marquer terminé
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {isInscrit && progression && (
        <div className="detail-progress">
          <div className="progress-header">
            <h3>Ma progression</h3>
            <span>{progression.progression}%</span>
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${progression.progression}%` }}
            ></div>
          </div>
          <p className="progress-status">
            Statut: <strong>{progression.statut === 'termine' ? 'Terminé' : 'En cours'}</strong>
          </p>
        </div>
      )}
    </div>
  );
};

export default DetailCours;